import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import {
  approveMarketingDraft,
  getMarketingDrafts,
  rejectMarketingDraft,
  type MarketingContentDraft,
} from '../lib/api';
import styles from './MarketingDrafts.module.css';

/**
 * Marketing drafts review — WO-3 Part C3 (docs/handoff/WO-3-launch-momentum.md,
 * J5 = "build-ui"). Admin-only card-list: browse drafts generated by the
 * scheduled digest job, approve or reject. Nothing else — no editing, no
 * send/publish button (that step is explicitly out of scope, its own future
 * JASON-GATE). Human-in-the-loop per the ANTITHESIS law: the machine drafts,
 * a human decides.
 *
 * Not linked from the tab bar (this is an operator surface, not a user-facing
 * tab) — reached directly at /admin/marketing-drafts. requireAuth+requireAdmin
 * on the API means a non-admin visiting this route just sees every fetch
 * 403 into the error state below; no separate client-side admin check is
 * invented here (the server is the single source of truth for admin-ness).
 */
export function MarketingDrafts() {
  const [drafts, setDrafts] = useState<MarketingContentDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOnId, setActingOnId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getMarketingDrafts('draft');
      setDrafts(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load drafts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleApprove(id: string) {
    setActingOnId(id);
    try {
      await approveMarketingDraft(id);
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed.');
    } finally {
      setActingOnId(null);
    }
  }

  async function handleReject(id: string) {
    setActingOnId(id);
    try {
      await rejectMarketingDraft(id);
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed.');
    } finally {
      setActingOnId(null);
    }
  }

  return (
    <div className={styles.page}>
      <Link to="/settings" className={styles.backButton}>
        ← Back
      </Link>
      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Operator</div>
        <div className={styles.title}>Marketing drafts</div>
      </div>
      <p className={styles.subtitle}>
        Drafted from real product activity, never sent or published. Approve or reject each one —
        nothing here leaves this screen on its own.
      </p>

      {loading && <div className={styles.status}>Loading drafts…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && drafts.length === 0 && (
        <div className={styles.status}>No drafts waiting on review.</div>
      )}

      {drafts.map((draft) => (
        <Card key={draft.id} className={styles.draftCard}>
          <div className={styles.draftKind}>{draft.kind}</div>
          <div className={styles.draftTitle}>{draft.title}</div>
          <div className={styles.draftBody}>{draft.body}</div>
          {draft.sourceRefs && draft.sourceRefs.length > 0 && (
            <div className={styles.sourceRefs}>
              {draft.sourceRefs.map((ref, i) => (
                <span key={i} className={styles.sourceRefChip}>
                  {ref.description}
                  {ref.value !== undefined ? `: ${ref.value}` : ''}
                </span>
              ))}
            </div>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.approveButton}
              disabled={actingOnId === draft.id}
              onClick={() => handleApprove(draft.id)}
            >
              Approve
            </button>
            <button
              type="button"
              className={styles.rejectButton}
              disabled={actingOnId === draft.id}
              onClick={() => handleReject(draft.id)}
            >
              Reject
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}
